import { FC } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import Box from '@amzn/awsui-components-core/box';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import StatusIndicator from '@amzn/awsui-components-core/status-indicator';
import { CampaignData } from 'src/@types/valueMatching';
import { useTranslate } from 'src/hooks/useTranslate';
import {formatEpochToIST} from "src/utils/commonUtils";

export interface AuditInfoSectionProps {
    /** Full campaign data object */
    campaignData: CampaignData;
}

/**
 * Audit info section — displays createdBy, createdTime, lastModifiedBy,
 * lastModifiedTime, and hasCampaignGoneLive.
 * Shared between View and Modify pages.
 * Owns its own i18n bundle (`organisms.auditInfoSection`).
 */
const AuditInfoSection: FC<AuditInfoSectionProps> = ({ campaignData }) => {
    const { translate } = useTranslate('organisms.auditInfoSection');

    return (
        <Container header={<Header variant="h2">{translate('section_title')}</Header>}>
            <ColumnLayout columns={3}>
                <SpaceBetween size="xs">
                    <Box variant="h4">{translate('created_by_label')}</Box>
                    <Box data-testid="created-by">{campaignData.createdBy ?? '—'}</Box>
                </SpaceBetween>
                <SpaceBetween size="xs">
                    <Box variant="h4">{translate('created_time_label')}</Box>
                    <Box data-testid="created-time">
                        {campaignData.createdTime != null
                            ? formatEpochToIST(campaignData.createdTime)
                            : '—'}
                    </Box>
                </SpaceBetween>
                <SpaceBetween size="xs">
                    <Box variant="h4">{translate('last_modified_by_label')}</Box>
                    <Box data-testid="last-modified-by">
                        {campaignData.lastModifiedBy ?? '—'}
                    </Box>
                </SpaceBetween>
                <SpaceBetween size="xs">
                    <Box variant="h4">{translate('last_modified_time_label')}</Box>
                    <Box data-testid="last-modified-time">
                        {campaignData.lastModifiedTime != null
                            ? formatEpochToIST(campaignData.lastModifiedTime)
                            : '—'}
                    </Box>
                </SpaceBetween>
                <SpaceBetween size="xs">
                    <Box variant="h4">{translate('has_gone_live_label')}</Box>
                    <Box data-testid="has-gone-live">
                        {campaignData.hasCampaignGoneLive == null ? (
                            '—'
                        ) : campaignData.hasCampaignGoneLive ? (
                            <StatusIndicator type="success">
                                {translate('yes')}
                            </StatusIndicator>
                        ) : (
                            <StatusIndicator type="stopped">
                                {translate('no')}
                            </StatusIndicator>
                        )}
                    </Box>
                </SpaceBetween>
            </ColumnLayout>
        </Container>
    );
};

export default AuditInfoSection;
